import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaignsRequest } from '../redux/slices/campaignsSlice';
import type { RootState } from '../redux/store';

/**
 * Smart hook for managing campaigns
 * 
 * Features:
 * - Avoids redundant API calls across pages
 * - Uses cached data when available
 * - Only shows loading state on initial fetch
 * - Automatically fetches if data is empty
 * 
 * Usage:
 * const { campaigns, isInitialLoading } = useCampaigns();
 * 
 * - `campaigns`: The campaigns array from Redux
 * - `isInitialLoading`: Only true during the very first fetch, false on subsequent page visits
 */
export const useCampaigns = () => {
  const dispatch = useDispatch();
  
  const { data: campaigns, loading, error, isFetched } = useSelector(
    (state: RootState) => state.campaigns
  );
  
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  useEffect(() => {
    // Only fetch if:
    // 1. We have a restaurantId
    // 2. Data hasn't been fetched yet (isFetched is false)
    // 3. Not currently loading
    if (restaurantId && !isFetched && !loading) {
      dispatch(fetchCampaignsRequest());
    }
  }, [dispatch, restaurantId, isFetched, loading]);

  return {
    campaigns,
    // Only show loading on initial fetch, not on subsequent page visits
    isInitialLoading: loading && !isFetched,
    // Always return error
    error,
    // Return whether data exists (for pages to know if they can render)
    hasData: campaigns.length > 0,
    // Return isFetched flag
    isFetched
  };
};
